import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import Swal from 'sweetalert2';

import isEmpty from '../../utils/isEmpty';
import formatString from '../../utils/formatString';
import { setColorTransparency, enfasisObjectTag } from '../../utils/fabricJsCustom';
import { get_organization_info } from '../../actions/organizationActions';
import { Icons } from './Icons';

const TagRow = ({ tag, visible, onToggle, onEmphasis, orgHeaders }) => {
	const [r, g, b] = tag.arrayRGBColor || [120, 120, 120];
	const colorBg = `rgb(${r}, ${g}, ${b})`;
	const hasMeta = orgHeaders.length > 0 && tag.data;

	return (
		<div className={`dn-tag-row${visible ? '' : ' dn-tag-row--hidden'}`}>
			<button
				type='button'
				className='dn-tag-eye'
				onClick={onToggle}
				aria-pressed={visible}
				title={visible ? 'Hide' : 'Show'}
			>
				{visible ? <Icons.eye /> : <Icons.eyeOff />}
			</button>
			<span className='dn-tag-color' style={{ background: colorBg }} aria-hidden='true' />
			<button
				type='button'
				className='dn-tag-name'
				onClick={onEmphasis}
				title={tag.className}
			>
				<span className='dn-tag-name-text'>{tag.className}</span>
				{hasMeta && (
					<span className='dn-tag-meta'>
						{orgHeaders.map((h, i) => (
							<span key={h} className='dn-tag-meta-item'>
								{i > 0 && <span className='dn-tag-meta-sep'>·</span>}
								<span className='dn-tag-meta-label'>{formatString(h)}:</span>{' '}
								<span className='dn-tag-meta-value'>{tag.data?.[h] ?? '—'}</span>
							</span>
						))}
					</span>
				)}
			</button>
			<span className='dn-tag-qty'>{tag.quantity}</span>
		</div>
	);
};

TagRow.propTypes = {
	tag: PropTypes.object.isRequired,
	visible: PropTypes.bool.isRequired,
	onToggle: PropTypes.func.isRequired,
	onEmphasis: PropTypes.func.isRequired,
	orgHeaders: PropTypes.array.isRequired,
};

const TagList = ({ canvas, resultTagsLoading, appOrgConfig, tagsList, selectedResult, imageInfo }) => {
	const { t } = useTranslation();
	const dispatch = useDispatch();
	const { image_info } = useSelector((s) => s.image);

	const [allVisible, setAllVisible] = useState(true);
	const [visibilityMap, setVisibilityMap] = useState({});
	const [orgHeaders, setOrgHeaders] = useState([]);
	const [doorsVisible, setDoorsVisible] = useState(false);

	const { productCount, frontCount } = useMemo(() => {
		const products = tagsList?.length || 0;
		const fronts = (tagsList || []).reduce((sum, tag) => sum + (tag.quantity || 0), 0);
		return { productCount: products, frontCount: fronts };
	}, [tagsList]);

	const isDoorTag = (tag) => /puerta/i.test(tag.className);

	const hasDoorTags = useMemo(() => (tagsList || []).some(isDoorTag), [tagsList]);

	const sortedTagsList = useMemo(() => {
		return [...(tagsList || [])].sort((a, b) => (b.quantity || 0) - (a.quantity || 0));
	}, [tagsList]);

	useEffect(() => {
		if (image_info?.organization?.$oid) {
			dispatch(get_organization_info(image_info.organization.$oid));
		}
	}, [image_info]);

	useEffect(() => {
		if (!isEmpty(imageInfo) && appOrgConfig) {
			const cfg = appOrgConfig?.data?.display_results?.find((r) => r.r_type_id === selectedResult?.r_type_id);
			setOrgHeaders(cfg ? cfg.table_headers : []);
		} else {
			setOrgHeaders([]);
		}
	}, [tagsList, imageInfo, appOrgConfig, selectedResult]);

	useEffect(() => {
		const map = {};
		(tagsList || []).forEach((tag) => {
			const visible = !isDoorTag(tag);
			map[tag.id] = visible;
			tag.isVisible = visible;
		});
		setVisibilityMap(map);
		setAllVisible(true);
		setDoorsVisible(false);
	}, [tagsList]);

	useEffect(() => {
		if (!canvas?._objects || !tagsList?.length) return;
		(tagsList || []).filter(isDoorTag).forEach((tag) => {
			canvas._objects.forEach((obj) => {
				if (obj.type === 'percepthorArticle' && obj.tag === tag) {
					obj.visible = false;
				}
			});
		});
		canvas.requestRenderAll();
	}, [canvas, tagsList]);

	const applyTagVisibility = useCallback((tagId, isVisible) => {
		const tag = (tagsList || []).find((t) => t.id === tagId);
		if (!tag) return;
		tag.isVisible = isVisible;
		if (canvas?._objects) {
			canvas._objects.forEach((obj) => {
				if (obj.type === 'percepthorArticle' && obj.tag === tag) {
					obj.visible = isVisible;
				}
			});
			canvas.requestRenderAll();
		}
	}, [canvas, tagsList]);

	const toggleAll = () => {
		const next = !allVisible;
		setAllVisible(next);
		const map = {};
		(tagsList || []).forEach((tag) => {
			const visible = next && (!isDoorTag(tag) || doorsVisible);
			map[tag.id] = visible;
			applyTagVisibility(tag.id, visible);
		});
		setVisibilityMap(map);
	};

	const toggleDoors = () => {
		const next = !doorsVisible;
		setDoorsVisible(next);
		const updates = {};
		(tagsList || []).forEach((tag) => {
			if (isDoorTag(tag)) {
				updates[tag.id] = next;
				applyTagVisibility(tag.id, next);
			}
		});
		setVisibilityMap((m) => ({ ...m, ...updates }));
	};

	const toggleOne = (tagId) => {
		const current = visibilityMap[tagId] ?? true;
		const next = !current;
		setVisibilityMap((m) => ({ ...m, [tagId]: next }));
		applyTagVisibility(tagId, next);
	};

	const adjustTransparency = () => {
		Swal.fire({
			title: t('tagList.colorSwal.title'),
			text: t('tagList.colorSwal.text'),
			input: 'range',
			inputValue: 0.5,
			inputAttributes: { min: 0, max: 1, step: 0.1 },
		}).then((result) => {
			if (result.value !== undefined && canvas) {
				setColorTransparency(Number(result.value));
				canvas.getObjects().forEach((obj) => {
					if (obj.type === 'percepthorArticle') obj.refreshColor();
				});
				canvas.requestRenderAll();
			}
		});
	};

	if (resultTagsLoading) {
		return (
			<div className='dn-taglist-state'>
				<div className='dn-taglist-spinner' aria-hidden='true' />
				<p>{t('display.loading', { defaultValue: 'Loading…' })}</p>
			</div>
		);
	}

	if (!tagsList || tagsList.length === 0) {
		return (
			<div className='dn-recon-empty'>
				<Icons.list />
				<p>{t('tagList.errors.noProducts', { defaultValue: 'No products detected.' })}</p>
			</div>
		);
	}

	return (
		<div className='dn-taglist'>
			<div className='dn-taglist-header'>
				<div className='dn-taglist-heading'>
					<div className='dn-taglist-title'>{t('display.taglist.title', { defaultValue: 'Detected items' })}</div>
					<div className='dn-taglist-metrics'>
						<span className='dn-taglist-metric'>
							<span className='dn-taglist-metric-value'>{productCount.toLocaleString()}</span>
							<span className='dn-taglist-metric-label'>{t('display.taglist.products', { defaultValue: 'Products' })}</span>
						</span>
						<span className='dn-taglist-metric-sep'>·</span>
						<span className='dn-taglist-metric'>
							<span className='dn-taglist-metric-value'>{frontCount.toLocaleString()}</span>
							<span className='dn-taglist-metric-label'>{t('display.taglist.fronts', { defaultValue: 'Fronts' })}</span>
						</span>
					</div>
				</div>
				<div className='dn-taglist-actions'>
					{hasDoorTags && (
						<label className='dn-switch' title={t('display.taglist.doors', { defaultValue: 'Doors' })}>
							<input type='checkbox' checked={doorsVisible} onChange={toggleDoors} />
							<span className='dn-switch-track' />
							<span className='dn-switch-label'>{t('display.taglist.doors', { defaultValue: 'Doors' })}</span>
						</label>
					)}
					<button
						type='button'
						className='dn-iconbtn'
						onClick={toggleAll}
						title={allVisible ? t('display.taglist.hideAll', { defaultValue: 'Hide all' }) : t('display.taglist.showAll', { defaultValue: 'Show all' })}
					>
						{allVisible ? <Icons.eyeOff /> : <Icons.eye />}
					</button>
					<button
						type='button'
						className='dn-iconbtn'
						onClick={adjustTransparency}
						title={t('display.taglist.opacity', { defaultValue: 'Adjust opacity' })}
					>
						<Icons.opacity />
					</button>
				</div>
			</div>
			<div className='dn-taglist-rows'>
				{sortedTagsList.map((tag) => (
					<TagRow
						key={tag.id.toString()}
						tag={tag}
						visible={visibilityMap[tag.id] ?? true}
						onToggle={() => toggleOne(tag.id)}
						onEmphasis={() => enfasisObjectTag(canvas, tag, 2500)}
						orgHeaders={orgHeaders}
					/>
				))}
			</div>
		</div>
	);
};

TagList.propTypes = {
	canvas: PropTypes.object,
	resultTagsLoading: PropTypes.bool.isRequired,
	appOrgConfig: PropTypes.object,
	tagsList: PropTypes.array.isRequired,
	selectedResult: PropTypes.object.isRequired,
	imageInfo: PropTypes.object.isRequired,
};

export default TagList;
